import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, withLoading } from 'src/lib/utils';
import {
  createQueryablePromiseFromPromise,
  ImageView,
  View,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import CareActionButton, { CareActionSelection } from './CareActionButton';
import StateObserver from 'src/StateObserver';
import platform, { analytics } from '@play-co/gcinstant';
import { careActions } from '../care/care.redux';
import statePromise from '../../lib/statePromise';
import { Pet, PetAttribute, PetStatusEffect } from 'src/replicant/buruburu/pet';
import { PartialRecord } from '@play-co/timestep-core';
import { getCareActionFinishEvent } from 'src/replicant/buruburu/care/care.analytics';
import { Visit } from 'src/replicant/visit';
import { GrantGemsReturn } from 'src/replicant/visit/visit.actions';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { startConversation } from '../conversation/conversation.redux';
import { VisitRecord } from '../../replicant/visit/record';
import { getFriendsPet } from '../pet';
import { petActions } from '../pet/pet.redux';
import { getMutex } from 'src/replicant/utils/mutex';

const ui = uiConfig.buruburu.menu;

export default class CareMenu extends FlexBoxImageScaleView {
  private mutex = getMutex();

  constructor() {
    super(ui.menu);

    this.image.onInputStart = () => this.emit('Close');

    // Add menu buttons
    const { onSelect } = this;
    const firstRow = this.addSubview(new FlexBoxView(ui.menuRow));
    firstRow.addSubview(new CareActionButton({ type: 'food', onSelect }));
    firstRow.addSubview(new CareActionButton({ type: 'snack', onSelect }));
    firstRow.addSubview(new CareActionButton({ type: 'toy', onSelect }));

    const secondRow = this.addSubview(new FlexBoxView(ui.menuRow));
    secondRow.addSubview(
      new CareActionButton({
        type: 'firstAid',
        onSelect,
      }),
    );
    secondRow.addSubview(new CareActionButton({ type: 'bath', onSelect }));
    secondRow.addSubview(new CareActionButton({ type: 'toilet', onSelect }));

    const closeHint = this.addSubview(new ImageView(ui.closeHintBg));
    closeHint.canHandleEvents(false, true);
    getFlexNode(closeHint).updateStyle({ marginTop: 23 });
    closeHint.addSubview(
      new LangBitmapFontTextView({
        ...ui.closeHintText,
        localeText: () => i18n('footer.tapToClose'),
        width: closeHint.style.width,
        height: closeHint.style.height,
      }),
    );
  }

  fadeIn(superview: View) {
    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  fadeOut() {
    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect = async ({ type, careItemId }: CareActionSelection) => {
    if (this.mutex.isLocked()) return;
    await this.mutex.lock();

    this.emit('Close');

    try {
      StateObserver.dispatch(petActions.setIsUpdating(true));

      const state = StateObserver.getState();
      let { user } = state;

      const previousVisibleState = Pet.getters(user).getPetState(
        StateObserver.now(),
        true,
      );

      const {
        consumed,
        resolvedStatusEffect,
        targetStatusEffects,
      } = await this.consumeCareActionSelection({
        type,
        careItemId,
      });

      if (consumed) {
        const { pet, level } = state;

        const { friendId } = user.visit.visitSession ?? {};
        const friendAttributes = pet[friendId]?.attributes;
        const friendAppearance = pet[friendId];
        const friendLevel = level[friendId];
        const { getVisitedCount, getVisitsCount } = VisitRecord.getters;

        const cureAttempted = targetStatusEffects !== undefined;
        const result = resolvedStatusEffect ? 'success' : 'failure';

        const { eventProperties, eventType } = getCareActionFinishEvent({
          action_type: type === 'firstAid' ? 'first_aid' : type,
          item_id: careItemId,
          result: cureAttempted ? result : undefined,
          withFriends: !!friendId,
          friendID: friendId,
          friendPetID: friendAppearance?.species,
          friendPetStomach: friendAttributes?.stomach,
          friendPetLove: friendAttributes?.love,
          friendDirtiness: friendAttributes?.dirtiness,
          friendUrine: friendAttributes?.urine,
          friendStress:
            friendAttributes && Pet.ruleset.getStressValue(friendAttributes),
          friendToothAcidity: friendAttributes?.toothAcidity,
          friendAge: friendAttributes?.age,
          friendPlayerLevel: friendLevel?.current,
          visits: friendId && getVisitsCount(user, friendId),
          visited: friendId && getVisitedCount(user, friendId),
        });
        analytics.pushEvent(eventType, eventProperties);

        const gemsPromise = createQueryablePromiseFromPromise(
          friendId
            ? StateObserver.invoke.asyncSyncFriendState()
            : Promise.resolve(null as GrantGemsReturn),
        );

        StateObserver.dispatch(
          careActions.setCareAction({
            type,
            item: careItemId,
          }),
        );

        await statePromise(({ care }) => !care.currentCareAction);

        // Use state after action is applied.
        user = StateObserver.getState().user;
        const { getPetState, getCurrentEvolution } = Pet.getters(user);

        const currentVisibleState = getPetState(StateObserver.now(), true);

        // Make sure item tried to cure visible state.
        const triedToCureSickness =
          targetStatusEffects?.includes('sick') &&
          previousVisibleState === 'sick';
        const triedToCureToothDecay =
          targetStatusEffects?.includes('toothDecay') &&
          previousVisibleState === 'tooth_decay';
        const triedToCureDying =
          targetStatusEffects?.includes('dying') &&
          previousVisibleState === 'dying';

        // Only show dialog if item tried to cure visible state.
        if (triedToCureSickness || triedToCureToothDecay || triedToCureDying) {
          const visibleStateChanged =
            currentVisibleState !== previousVisibleState;

          await startConversation(
            platform.playerID,
            visibleStateChanged ? 'cured' : 'not_cured',
            getCurrentEvolution().species,
            previousVisibleState,
          );
        }

        // Show loading screen while gems are being granted.
        if (!gemsPromise.isReady()) {
          await withLoading(() => gemsPromise);
        }

        // Use return from grant gems to update attributes.
        await this.updateFriendPetStats(await gemsPromise);
      }
    } finally {
      StateObserver.dispatch(petActions.setIsUpdating(false));
      this.mutex.release();
    }
  };

  private async consumeCareActionSelection({
    type,
    careItemId,
  }: CareActionSelection): Promise<{
    consumed: boolean;
    resolvedStatusEffect?: boolean;
    targetStatusEffects?: PetStatusEffect[];
    friendDiff?: {
      statusEffects: PartialRecord<PetStatusEffect, boolean>;
      attributes: PartialRecord<PetAttribute, number>;
    };
  }> {
    switch (type) {
      case 'bath':
      case 'toilet': {
        return StateObserver.invoke.performCareAction(type);
      }

      default:
        return StateObserver.invoke.purchaseCareItem(careItemId);
    }
  }

  private async updateFriendPetStats(updatePayload: GrantGemsReturn) {
    const state = StateObserver.getState();
    const { visitSession } = state.user.visit;

    if (!visitSession) {
      return;
    }

    const { friendId: id } = visitSession;
    const { appearance } = getFriendsPet();

    appearance.setAttributes(updatePayload.petDetails.attributes);
    appearance.setStatusEffects(updatePayload.petDetails.statusEffects);

    if (updatePayload.granted) {
      await startConversation(
        id,
        'friend_appreciation',
        state.pet[id].species,
        'normal',
      );

      await openPopupPromise('popupVisitReward', {
        reward: Visit.ruleset.gemsReward,
      });
      await StateObserver.invoke.claimVisitGems();
    }
  }
}
